import * as React from 'react'
import { useFlowium } from '../client/FlowiumContext'
import { Workspace, Project } from '@flowium/client'
import { PlusIcon, ArchiveAltIcon, NotificationIcon, LayersIcon, DocsIcon } from '../components/Icons'
import UpgradeButton from '../components/UpgradeButton'
import UserMenu from '../components/UserMenu'
import CreateWorkspaceModal from '../components/CreateWorkspaceModal'
import CreateProjectModal from '../components/CreateProjectModal'
import LoadingPage from '../components/LoadingPage'
import NotificationPanel from '../components/NotificationPanel'
import WorkspaceIcon from '../components/WorkspaceIcon'
import { useNavigate } from 'react-router'
import { useState, useEffect } from 'react'
import Avatar from '../components/Avatar'
import Button from '../components/Button'
import { getDisplayLabel } from '../utils'

interface WorkspacesListProps {
    workspaces: Workspace[]
    currentWorkspace: Workspace | undefined
    onWorkspaceChange: (workspaceId: string) => void
}

const WorkspacesList: React.FC<WorkspacesListProps> = ({ workspaces, currentWorkspace, onWorkspaceChange }) => {
    const defaultWorkspace = workspaces.find(w => w.name.trim() === 'Default')
    const otherWorkspaces = workspaces.filter(w => w.name.trim() !== 'Default')

    return (
        <div className="flex flex-col items-center space-y-2">
            {defaultWorkspace && (
                <WorkspaceIcon
                    key={defaultWorkspace.id}
                    workspace={defaultWorkspace}
                    isActive={currentWorkspace?.id === defaultWorkspace.id}
                    onClick={() => onWorkspaceChange(defaultWorkspace.id)}
                />
            )}
            {otherWorkspaces.map((workspace) => (
                <WorkspaceIcon
                    key={workspace.id}
                    workspace={workspace}
                    isActive={currentWorkspace?.id === workspace.id}
                    onClick={() => onWorkspaceChange(workspace.id)}
                />
            ))}
        </div>
    )
}

interface ProjectsListProps {
    currentProject: Project | undefined
    projects: Project[]
    onProjectChange: (projectId: string) => void
}

const ProjectsList: React.FC<ProjectsListProps> = ({ currentProject, projects, onProjectChange }) => {
    const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = React.useState(false)
    return (
        <div className="flex h-full flex-col">
            <div className="border-b border-secondary px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-content-tertiary">Projects</h3>
                        <p className="mt-1 text-sm text-content-secondary">
                            Switch between project scopes in the current workspace.
                        </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setIsCreateProjectModalOpen(true)}>
                        New
                    </Button>
                </div>
            </div>

            <ul className="flex-1 space-y-1 overflow-y-auto px-3 py-3">
                {projects.length > 0 ? (
                    projects.map((project) => (
                        <li key={project.id}>
                            <button
                                type="button"
                                className={`flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-sm transition-colors ${
                                    currentProject?.id === project.id
                                        ? 'bg-surface-secondary text-content shadow-sm'
                                        : 'text-content-secondary hover:bg-hover hover:text-content'
                                }`}
                                onClick={() => onProjectChange(project.id)}
                            >
                                <span className="min-w-0 truncate font-medium">{getDisplayLabel(project.name, 'Untitled project')}</span>
                            </button>
                        </li>
                    ))
                ) : (
                    <li className="rounded-2xl border border-dashed border-secondary bg-surface-secondary px-4 py-5 text-center">
                        <div className="text-sm text-content-secondary">No projects exist for this workspace.</div>
                        <Button
                            variant="secondary"
                            size="sm"
                            className="mt-3"
                            onClick={() => setIsCreateProjectModalOpen(true)}
                        >
                            Create Project
                        </Button>
                    </li>
                )}
                {currentProject && (
                    <li className="px-1 pt-2">
                        <Button variant="link" size="sm" onClick={() => onProjectChange('')}>
                            Deselect project
                        </Button>
                    </li>
                )}
            </ul>

            <CreateProjectModal
                isOpen={isCreateProjectModalOpen}
                onClose={() => setIsCreateProjectModalOpen(false)}
            />
        </div>
    )
}

const StorageOverviewSection = () => {
    const { user } = useFlowium()
    if (!user) return null

    return (
        <div className="rounded-2xl border border-secondary bg-surface-secondary p-4">
            <div className="mb-2 flex items-center justify-between text-sm text-content-secondary">
                <span className="text-lg font-semibold capitalize text-content">{user.plan || 'Free'}</span>
                <div className="flex items-center space-x-2">
                    <LayersIcon className="text-content-secondary" selectable={false} padding={0} />
                    <span className="text-sm">Files</span>
                    <ArchiveAltIcon className="text-content-secondary" selectable={false} />
                </div>
            </div>

            <div className="relative mt-4 flex items-center">
                <div className="mr-4 h-2 w-full flex-grow rounded-full bg-surface">
                    <div
                        className="h-2 rounded-full bg-accent"
                        style={{ width: `${(user.number_of_files / user.file_limit) * 100}%` }}
                    />
                </div>
                <span className="text-content-secondary font-light text-sm flex-shrink-0">{user.file_limit}</span>
            </div>

            <div className="mt-3 text-sm font-light text-content-secondary">
                <span className="text-content">{user.number_of_files}</span> of <span className="text-content">{user.file_limit}</span> files used
            </div>

            <UpgradeButton text="Upgrade to Pro" className="mt-4" />
        </div>
    )
}

const Sidebar: React.FC = () => {
    const { client, currentWorkspace, currentProject, projects, workspaces, user, fetchUserData, logout, showLoading, hideLoading } = useFlowium()
    const [isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen] = useState(false)
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
    const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false)
    const [unreadNotifications, setUnreadNotifications] = useState(0)
    const navigate = useNavigate()
    const bellRef = React.useRef<HTMLDivElement>(null)
    const userMenuRef = React.useRef<HTMLDivElement>(null)

    useEffect(() => {
        let cancelled = false
        const fetchUnreadNotifications = async () => {
            try {
                const { notifications } = await client.getNotifications(1, 20)
                const unread = notifications.filter(n => !n.read).length
                if (cancelled) return
                setUnreadNotifications(unread)
            } catch (err) {
                // silently handled
            }
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchUnreadNotifications()
            }
        }

        fetchUnreadNotifications()
        const interval = setInterval(fetchUnreadNotifications, 60000)
        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            cancelled = true
            clearInterval(interval)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [client])

    const handleProjectChange = async (projectId: string) => {
        const workspaceId = currentWorkspace?.id || user?.workspace
        if (!workspaceId) return
        showLoading()
        try {
            await client.setProject(projectId, workspaceId)
            await fetchUserData()
            navigate('/browser')
        } catch (err) {
            if (import.meta.env.DEV) console.error('Failed to switch project', err)
        } finally {
            hideLoading()
        }
    }

    const handleWorkspaceChange = async (workspaceId: string) => {
        showLoading()
        try {
            await client.setWorkspace(undefined, workspaceId)
            await fetchUserData()
            navigate('/browser')
        } catch (err) {
            if (import.meta.env.DEV) console.error('Failed to switch workspace', err)
        } finally {
            hideLoading()
        }
    }

    const handleUserMenuClick = () => {
        setIsUserMenuOpen(true)
    }

    const handleLogout = () => {
        logout()
        navigate('/login')
    }

    if (!user) {
        return <LoadingPage />
    }

    return (
        <>
            <aside className="hidden w-16 shrink-0 flex-col items-center border-r border-secondary bg-surface-secondary lg:flex">
                <div className="mt-4">
                    <button
                        type="button"
                        className="flex h-11 w-11 items-center justify-center rounded-xl border border-secondary bg-surface text-content transition-colors hover:bg-hover"
                        onClick={() => setIsCreateWorkspaceModalOpen(true)}
                        aria-label="Create workspace"
                    >
                        <PlusIcon padding={0} />
                    </button>
                </div>

                <div className="mt-3 flex flex-col items-center gap-2">
                    <button
                        type="button"
                        className="flex h-11 w-11 items-center justify-center rounded-xl border border-accent bg-accent/5 text-content shadow-sm"
                        onClick={() => navigate('/browser')}
                        aria-label="Browse files"
                    >
                        <DocsIcon active={true} padding={0} />
                    </button>
                </div>

                <div className="mt-6 flex flex-col items-center space-y-2">
                    <WorkspacesList
                        workspaces={workspaces}
                        currentWorkspace={currentWorkspace}
                        onWorkspaceChange={handleWorkspaceChange}
                    />
                </div>

                <div className="mt-auto mb-4 flex flex-col items-center gap-3">
                    <div className="relative" ref={bellRef}>
                        <button
                            type="button"
                            onClick={() => setIsNotificationPanelOpen((v) => !v)}
                            aria-label="Notifications"
                            className="flex h-11 w-11 items-center justify-center rounded-xl border border-transparent bg-transparent transition-colors hover:border-secondary hover:bg-hover"
                        >
                            <NotificationIcon active={unreadNotifications > 0} padding={0} />
                        </button>
                        {unreadNotifications > 0 && (
                            <div className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full bg-error" aria-label="Unread notifications"></div>
                        )}
                    </div>
                    <div ref={userMenuRef}>
                        <button
                            type="button"
                            onClick={handleUserMenuClick}
                            className="rounded-full"
                            aria-label="User menu"
                        >
                            <Avatar color="#52525B" label={user.email.charAt(0).toUpperCase()} />
                        </button>
                    </div>
                    <UserMenu
                        isOpen={isUserMenuOpen}
                        onClose={() => setIsUserMenuOpen(false)}
                        anchorRef={userMenuRef}
                        onLogout={handleLogout}
                        onSettings={() => navigate('/settings')}
                    />
                </div>

                <NotificationPanel
                    isOpen={isNotificationPanelOpen}
                    onClose={() => setIsNotificationPanelOpen(false)}
                    anchorRef={bellRef}
                />
            </aside>
            <div className="hidden w-72 shrink-0 flex-col border-r border-secondary bg-surface lg:flex">
                <div className="border-b border-secondary px-5 py-5">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-content-tertiary">Workspace</div>
                    <div className="mt-1 truncate text-lg font-semibold text-content">{getDisplayLabel(currentWorkspace?.name, 'Flowium')}</div>
                </div>
                <div className="min-h-0 flex-1">
                    <ProjectsList
                        currentProject={currentProject}
                        projects={projects}
                        onProjectChange={handleProjectChange}
                    />
                </div>
                <div className="border-t border-secondary px-5 py-5">
                    <StorageOverviewSection />
                </div>
                <CreateWorkspaceModal
                    isOpen={isCreateWorkspaceModalOpen}
                    onClose={() => setIsCreateWorkspaceModalOpen(false)}
                />
            </div>
        </>
    )
}

export default Sidebar
